import type { InternalAxiosRequestConfig } from "axios";

import axios, { isAxiosError } from "axios";

import { normalizeAxiosError } from "@/api/api-error-normalization.ts";
import { createBackofficeApi } from "@/api/backoffice/backoffice-api-builder.ts";
import { tokenResponseSchema } from "@/api/backoffice/login/backoffice-external-login-api.ts";
import { loadDeprecatedEnv } from "@/env.deprecated.ts";
import {
  retrieveTokenDetailsFromLocalStorage,
  saveTokenDetailsInLocalStorage,
} from "@/lib/helpers/authentication-helper.ts";
import { SessionExpiredError } from "@/lib/session-expired-error";
import { isDefined } from "@/types/utils";

const deprecatedEnv = loadDeprecatedEnv();
const VOLTACALL_SERVICE_ID = deprecatedEnv.VITE_VOLTACALL_SERVICE_ID;
// truthy if null
const ADD_TRAILING_SLASH =
  !isDefined(deprecatedEnv.VITE_ADD_TRAILING_SLASH) || deprecatedEnv.VITE_ADD_TRAILING_SLASH === true;

/**
 * V1 of the backoffice client.
 *
 * Written at a time where there was no OpenAPI specification we could rely on.
 * Avoid adding new endpoints here and prefer :
 *  - Fetching the latest OpenAPI specification from the backend in the 'openapi' folder
 *  - Regenerating the client SDK with `mise run codegen`
 *  - Exposing the new endpoints in `backoffice-client.ts`
 */

const BackofficeApi = createBackofficeApi(deprecatedEnv);

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

BackofficeApi.axios.interceptors.request.use((config) => {
  if (ADD_TRAILING_SLASH && config.url && !config.url.endsWith("/")) {
    config.url += "/";
  }

  config.headers.set("serviceId", VOLTACALL_SERVICE_ID);
  config.headers.set("X-Voltalis-ServiceId", VOLTACALL_SERVICE_ID);

  const tokenDetails = retrieveTokenDetailsFromLocalStorage();
  if (!tokenDetails) {
    return config;
  }

  const { access_token: accessToken } = tokenDetails;

  config.headers.set("Authorization", `Bearer ${accessToken}`);

  return config;
});

BackofficeApi.axios.interceptors.response.use(
  (response) => {
    return Promise.resolve(response);
  },
  async (error) => {
    const tokenDetails = retrieveTokenDetailsFromLocalStorage();

    if (!tokenDetails) {
      throw normalizeAxiosError(error, "backoffice");
    }

    const { refresh_token: refreshToken } = tokenDetails;

    if (isAxiosError(error) && error.config) {
      const originalConfig = error.config as RetriableRequestConfig;

      if (error.response?.status === 401 && !originalConfig._retry) {
        originalConfig._retry = true;

        try {
          const resp = await axios.post(`${deprecatedEnv.VITE_BACKOFFICE_BASE_URL}/login/refresh`, {
            refresh_token: refreshToken,
          });
          const refreshedTokenDetails = tokenResponseSchema.parse(resp.data);

          saveTokenDetailsInLocalStorage(refreshedTokenDetails);

          originalConfig.headers.Authorization = `Bearer ${refreshedTokenDetails.access_token}`;
          return await BackofficeApi.axios(originalConfig);
        } catch (refreshError) {
          if (isAxiosError(refreshError) && refreshError.response?.status === 401) {
            throw new SessionExpiredError(refreshError);
          }

          throw normalizeAxiosError(refreshError, "backoffice");
        }
      }
    }

    throw normalizeAxiosError(error, "backoffice");
  },
);

export default BackofficeApi;
